<template>
    <div class="modal fade show modal-style" aria-labelledby="orderTagTitle">
        <div class="modal-dialog modal-dialog-centered" ref="modalRef">
            <div class="modal-content">
                <div class="row">
                    <div class="col-12">
                        <div class="d-flex justify-content-between px-1 my-1 align-item-center">
                            <h4 class="mb-0">Order Tags — {{ order.bill_number || order.order_number }}</h4>
                            <button
                                @click="onClickClose"
                                type="button"
                                class="btn-close"
                                aria-label="Close"
                            ></button>
                        </div>
                    </div>
                </div>

                <hr class="mt-0 mb-1">

                <div class="px-2 pb-2">
                    <label class="form-label">Tag</label>
                    <AsyncSelect
                        placeholder="Select Tag"
                        v-model="tag"
                        :api-service="noteNameByType"
                        :additional-query="tagQuery"
                    />

                    <div class="mt-1">
                        <label class="form-label">Reason</label>
                        <textarea
                            class="form-control"
                            rows="2"
                            v-model="reason"
                            placeholder="Reason"
                        ></textarea>
                    </div>

                    <div class="d-flex justify-content-end mt-2">
                        <button
                            :disabled="loading || !tag"
                            @click="onAddTag"
                            class="btn btn-primary"
                        >Add Tag</button>
                    </div>

                    <hr class="my-2">

                    <h5 class="mb-2"><i class="fas fa-tags me-1 text-primary"></i> Attached Tags</h5>
                    <div v-if="tagLoading" class="text-muted"><small>Loading...</small></div>
                    <div v-if="!tagLoading && !hasTags" class="text-center text-muted py-3">
                        <i class="fas fa-tag d-block mb-1" style="font-size: 22px; opacity: 0.4;"></i>
                        <small>No tags attached.</small>
                    </div>
                    <div v-if="!tagLoading && hasTags" class="tag-card-list">
                        <div
                            v-for="item in tags"
                            :key="item.id"
                            class="tag-card"
                        >
                            <div class="tag-card-body">
                                <span class="tag-chip"><i class="fas fa-tag me-1"></i>{{ item.tag_name }}</span>
                                <div v-if="item.reason" class="tag-reason">{{ item.reason }}</div>
                            </div>
                            <button
                                :disabled="loading"
                                class="btn btn-sm btn-outline-danger tag-remove"
                                title="Remove"
                                @click="onRemoveTag(item)"
                            >
                                <i class="fas fa-trash"></i>
                            </button>
                        </div>
                        <div v-if="hasMoreTags" class="text-center mt-2">
                            <button class="btn btn-sm btn-outline-primary" :disabled="tagLoadingMore" @click="loadMoreTags">
                                <i class="fas fa-chevron-down me-1"></i>
                                {{ tagLoadingMore ? 'Loading...' : 'Load More' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, inject } from 'vue'
import { useRoute } from 'vue-router'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import handlePurchase from '@/services/modules/purchase'
import handleHospital from '@/services/modules/hospital'

const props = defineProps({
    order: { type: Object, required: true }
})

const emit = defineEmits(['close', 'updated'])

const route = useRoute()
const showError = inject('showError')
const showSuccess = inject('showSuccess')

const {
    getEcommerceOrderActivity,
    updateEcommerceOrderTag
} = handlePurchase()
const { noteNameByType } = handleHospital()

const loading = ref(false)
const tagLoading = ref(false)
const tagLoadingMore = ref(false)
const tags = ref([])
const tag = ref(null)
const reason = ref('')
const tagQuery = ref({ type: 'order_tag' })
const tagPageSize = 3
const tagCurrentPage = ref(1)
const tagLastPage = ref(1)

const companyId = computed(() => route.params.companyId)

const hasTags = computed(() => tags.value.length > 0)

const hasMoreTags = computed(() => tagCurrentPage.value < tagLastPage.value)

const tagListQuery = (page) => `?company_id=${companyId.value}&type=tag&offset=${tagPageSize}&page=${page}`

const getTags = async () => {
    tagLoading.value = true
    const res = await getEcommerceOrderActivity(props.order.id, tagListQuery(1))
    tagLoading.value = false
    if (!res.status || !res.data) return
    tags.value = res.data.data
    tagCurrentPage.value = res.data.current_page
    tagLastPage.value = res.data.last_page
}

const loadMoreTags = async () => {
    if (tagLoadingMore.value) return
    tagLoadingMore.value = true
    const nextPage = tagCurrentPage.value + 1
    const res = await getEcommerceOrderActivity(props.order.id, tagListQuery(nextPage))
    tagLoadingMore.value = false
    if (!res.status || !res.data) return
    tags.value = tags.value.concat(res.data.data)
    tagCurrentPage.value = res.data.current_page
    tagLastPage.value = res.data.last_page
}

const onAddTag = async () => {
    if (!tag.value) return
    loading.value = true
    const res = await updateEcommerceOrderTag(props.order.id, {
        company_id: companyId.value,
        tag_id: tag.value.id,
        action: 'add',
        reason: reason.value
    })
    loading.value = false
    if (!res.status) {
        return showError(res.message)
    }
    showSuccess(res.message)
    tag.value = null
    reason.value = ''
    await getTags()
    emit('updated')
}

const onRemoveTag = async (item) => {
    loading.value = true
    const res = await updateEcommerceOrderTag(props.order.id, {
        company_id: companyId.value,
        tag_id: item.tag_id,
        action: 'remove'
    })
    loading.value = false
    if (!res.status) {
        return showError(res.message)
    }
    showSuccess(res.message)
    await getTags()
    emit('updated')
}

const onClickClose = () => {
    emit('close')
}

onMounted(() => {
    getTags()
})
</script>

<style scoped>
.modal-style {
    display: block;
    background: #1c1d1c33;
}
.tag-card-list {
    max-height: 280px;
    overflow-y: auto;
}
.tag-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    background: #f8f8fb;
    border: 1px solid #ebe9f1;
    border-radius: 8px;
    padding: 8px 12px;
    margin-bottom: 8px;
}
.tag-card-body {
    min-width: 0;
}
.tag-chip {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    font-weight: 600;
    color: #7367f0;
    background: #e7e6f7;
    border-radius: 999px;
    padding: 3px 10px;
}
.tag-reason {
    font-size: 12px;
    color: #6e6b7b;
    margin-top: 5px;
    padding-left: 2px;
}
.tag-remove {
    flex-shrink: 0;
}
</style>
